import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Button } from "@/components/ui/button";
import { Phone, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Hero image
import heroImage from "@/assets/brick-work-masonry-stairs-wayne-nj-hero.webp";
import northCaldwellBrickWork from "@/assets/brick-work-north-caldwell-essex-county-nj.webp";

// Placeholder for gallery images - replace these imports when images are uploaded
const placeholderImage = "/placeholder.svg";

// Citation Link Component
interface CitationLinkProps {
  href: string;
  children: React.ReactNode;
  number: number;
}

const CitationLink = ({ href, children, number }: CitationLinkProps) => (
  <>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-secondary hover:text-secondary/80 underline underline-offset-2"
    >
      {children}
    </a>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-secondary hover:text-secondary/80 text-xs align-super ml-0.5"
    >
      [{number}]
    </a>
  </>
);

const brickServices = [
  "New brick installation for homes, additions, and commercial buildings",
  "Brick repair, including tuckpointing and mortar joint restoration",
  "Custom features like brick columns, garden walls, and fire pits",
  "Chimney rebuilds and exterior facade restoration",
  "Front steps, walkways, and retaining walls",
];

const bondPatterns = [
  {
    name: "Running Bond",
    description: "The most widely used for residential walls in Essex and Morris County. Simple, clean, and strong.",
  },
  {
    name: "Flemish Bond",
    description: "Alternates headers and stretchers in each course. Common on historic properties in towns like Morristown.",
  },
  {
    name: "English Bond",
    description: "Alternates full courses of headers and stretchers. Good structural integrity for thicker walls.",
  },
  {
    name: "Stack Bond",
    description: "Bricks line up vertically. Modern look, but weaker and needs steel reinforcement.",
  },
  {
    name: "Header Bond",
    description: "All headers, often used for curved walls or decorative accents.",
  },
];

const brickGrades = [
  {
    grade: "Grade SW (Severe Weathering)",
    description: "Lowest water absorption, highest compressive strength. Built for climates like ours.",
    recommended: true,
  },
  {
    grade: "Grade MW (Moderate Weathering)",
    description: "Works in milder regions with less moisture exposure.",
    recommended: false,
  },
  {
    grade: "Grade NW (Negligible Weathering)",
    description: "Interior use only. Not rated for any outdoor application.",
    recommended: false,
  },
];

const faqs = [
  {
    question: "What types of brick work can a masonry contractor do in North Jersey?",
    answer: "A masonry contractor in North Jersey can handle new construction, chimney rebuilds, front steps, walkways, columns, retaining walls, tuckpointing, and full exterior facades for both residential and commercial properties. The scope depends on the project, but most brick-related work falls under what a licensed mason can do.",
  },
  {
    question: "Do I need a permit for brick work in North Jersey?",
    answer: "Structural masonry projects in New Jersey typically require a construction permit issued through the local building department under NJ DCA regulations. Minor repairs like repointing a few mortar joints may qualify as ordinary maintenance and not require a permit. When in doubt, your local building department can clarify what applies to your project.",
  },
  {
    question: "What is the best type of brick for North Jersey weather?",
    answer: "Grade SW (severe weathering) bricks are the best choice for North Jersey's climate. They meet ASTM C62 standards for freeze-thaw resistance and have the lowest water absorption rate of the three brick grades. For any exterior application in this region, Grade SW is the standard.",
  },
  {
    question: "How do I know if my brick wall needs repair?",
    answer: "Cracked mortar joints, spalling brick faces, white staining called efflorescence, and bowing are all signs that repair is needed. Some of these are cosmetic, but others point to water intrusion or structural movement. A masonry contractor can assess the damage and recommend whether tuckpointing, brick replacement, or a more involved repair makes sense.",
  },
  {
    question: "What should I look for when hiring a brick mason in North Jersey?",
    answer: "Look for NJ contractor licensing and insurance first. After that, ask about their experience with local building codes and whether they carry MCAA certification. Check reviews and ask to see photos of recent projects. A qualified mason should be able to explain their process and answer your questions without dodging details.",
  },
];

const citations = [
  {
    id: 1,
    source: "Brick Industry Association (BIA)",
    description: "Technical Notes on Brick Construction: industry-standard guidance on brick bonds, mortar selection, water resistance, and construction best practices.",
    url: "https://www.gobrick.com/resources/technical-notes",
  },
  {
    id: 2,
    source: "ASTM International",
    description: "ASTM C62: Standard Specification for Building Brick, including Grade SW/MW/NW weathering ratings, compressive strength, and water absorption requirements.",
    url: "https://www.astm.org/c0062-17.html",
  },
  {
    id: 3,
    source: "OSHA",
    description: "29 CFR 1926.706: Requirements for Masonry Construction, including limited access zones, wall bracing, and scaffolding requirements.",
    url: "https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.706",
  },
  {
    id: 4,
    source: "Mason Contractors Association of America (MCAA)",
    description: "Masonry Certification Program for identifying quality-focused masonry professionals.",
    url: "https://www.masoncontractors.org/",
  },
  {
    id: 5,
    source: "NJ Department of Community Affairs (NJ DCA)",
    description: "Construction Permit Application forms and local building department contacts under the NJ Uniform Construction Code.",
    url: "https://www.nj.gov/dca/divisions/codes/",
  },
];

const youtubeVideoIds = ["7N5f77AabdU"];

// Placeholder images - replace with actual images when uploaded
const galleryImages = [
  {
    src: placeholderImage,
    alt: "Brick work project placeholder 1",
  },
  {
    src: placeholderImage,
    alt: "Brick work project placeholder 2",
  },
  {
    src: placeholderImage,
    alt: "Brick work project placeholder 3",
  },
];

const BrickWork = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Brick Work Totowa, NJ | 35+ Years | Masonry Pro Construction</title>
        <meta name="description" content="Need brick work in North Jersey? We handle new builds, repairs, and restoration across Bergen, Passaic, Essex, and Morris counties. Call for a free estimate." />
        <meta property="og:description" content="Need brick work in North Jersey? We handle new builds, repairs, and restoration across Bergen, Passaic, Essex, and Morris counties. Call for a free estimate." />
        <meta property="og:title" content="Brick Work Totowa, NJ | 35+ Years | Masonry Pro Construction" />
        
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Brick Work",
            description: "Expert brick work in North Jersey including new builds, repairs, and restoration.",
            url: "/masonry-services/brick-work",
            image: "https://masonry-pro.com/og/brick-work.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/brick-work.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/brick-work.jpg" />
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
          <img src={heroImage} alt="Brick masonry construction in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
              <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Brick Work</span> Contractor in Totowa, NJ Serving North Jersey
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
              Residential and commercial brick masonry built for North Jersey's climate. New construction, repairs, and restoration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="xl" asChild>
                <a href="tel:+19737252395" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (973) 725-2395
                </a>
              </Button>
              <Button variant="goldOutline" size="xl" asChild>
                <a href="#contact">Get Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Brick Work Covers More Than Walls in North Jersey Homes
              </h2>
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                <p>
                  When most people hear "brick work," they think of walls. And that is a big part of it. But brick masonry covers a lot more than that. We build and repair chimneys, front steps, walkways, retaining walls, columns, and full exterior facades. Brick is the original trade behind our work as a <Link to="/" className="text-secondary hover:underline">masonry contractor in North Jersey</Link>.
                </p>
                <p>
                  For residential projects in Bergen County and Passaic County, the most common work we handle includes exterior wall construction, chimney rebuilds, garden walls, and mailbox columns. On the commercial side, we see storefronts, loading docks, and retaining walls.
                </p>
                <p>
                  A lot of towns across North Jersey have older homes with original brickwork. Places like Montclair and Ridgewood have properties that go back decades. When those need repair or restoration, matching the existing brick and mortar profile matters. We source matching materials so the repair looks like it belongs.
                </p>
                <p>
                  The <CitationLink href="https://www.gobrick.com/resources/technical-notes" number={1}>Brick Industry Association (BIA) Technical Notes</CitationLink> provide industry-standard definitions for brick construction types and installation methods. We follow those guidelines on every project.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                New Brick Installation and Repair Services
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                A licensed masonry contractor selects brick grade, bond pattern, and mortar type based on the structure and local building codes.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {brickServices.map((service, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border"
                >
                  <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bond Patterns Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                The Right Brick Bond Pattern Strengthens Your Structure
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-3xl mx-auto">
                A bond pattern is how bricks are arranged in a wall. It's not just decorative. The pattern you choose affects how well the wall holds up over time. Some patterns interlock better than others, which means more structural strength.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bondPatterns.map((pattern, index) => (
                  <div
                    key={index}
                    className="p-6 bg-card rounded-xl border border-border"
                  >
                    <h3 className="text-lg font-bold mb-2 text-secondary">{pattern.name}</h3>
                    <p className="text-muted-foreground text-sm">{pattern.description}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground text-center mt-8">
                <CitationLink href="https://www.gobrick.com/resources/technical-notes" number={1}>BIA Technical Note 30</CitationLink> covers bond pattern standards and structural considerations in detail.
              </p>
            </div>
          </div>
        </section>

        {/* Brick Grades Section */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                Grade SW Bricks Handle North Jersey Freeze-Thaw Cycles
              </h2>
              <p className="text-muted-foreground text-center mb-10 max-w-3xl mx-auto">
                Not all bricks are built for the same climate. North Jersey gets hit with over 80 freeze-thaw cycles per year. Choosing the wrong grade means early failure.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {brickGrades.map((grade, index) => (
                  <div
                    key={index}
                    className={`p-6 rounded-xl border ${
                      grade.recommended
                        ? "bg-secondary/10 border-secondary"
                        : "bg-card border-border"
                    }`}
                  >
                    <h3 className={`text-lg font-bold mb-2 ${grade.recommended ? "text-secondary" : ""}`}>
                      {grade.grade}
                    </h3>
                    <p className="text-muted-foreground text-sm">{grade.description}</p>
                    {grade.recommended && (
                      <span className="inline-block mt-3 text-xs font-semibold text-secondary bg-secondary/20 px-2 py-1 rounded">
                        Recommended for North Jersey
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground text-center mt-8">
                North Jersey falls in the severe weathering index under <CitationLink href="https://www.astm.org/c0062-17.html" number={2}>ASTM C62 classifications</CitationLink>.
              </p>
            </div>
          </div>
        </section>

        {/* Common Mistakes Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-10">
                Skilled Bricklaying Prevents the Most Common Installation Mistakes
              </h2>
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Text content */}
                <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                  <p>
                    Poor bricklaying shows up fast, sometimes within the first year. And the repairs cost more than doing it right the first time. We see the same mistakes over and over on jobs where the original crew cut corners.
                  </p>
                  <p>
                    The biggest issues are incomplete mortar joints, inconsistent joint thickness, poor alignment, and skipping weep holes in veneer walls. Out of all of those, incomplete mortar joints are the number one cause of brick wall failure.
                  </p>
                  <p>
                    Here is something a lot of homeowners don't realize: bricks are not waterproof on their own. Proper mortar coverage, flashing, and drainage detailing are what keep water out. Skip any one of those steps and the wall will fail eventually.
                  </p>
                  <p>
                    In New Jersey, structural masonry projects typically require a construction permit through the local building department. The <CitationLink href="https://www.nj.gov/dca/divisions/codes/" number={5}>NJ Uniform Construction Code</CitationLink>, enforced by the NJ Department of Community Affairs, sets the rules.
                  </p>
                  <p>
                    For safety requirements during construction, <CitationLink href="https://www.osha.gov/laws-regs/regulations/standardnumber/1926/1926.706" number={3}>OSHA standard 1926.706</CitationLink> covers limited access zones, wall bracing, and scaffolding for masonry operations.
                  </p>
                </div>
                {/* Image */}
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img
                    src={northCaldwellBrickWork}
                    alt="Newly constructed brick work stairs and porch at a home in North Caldwell, Essex County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Section */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Brick Adds Lasting Curb Appeal and Resale Value
              </h2>
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                <p>
                  There is a reason brick has been around for thousands of years. It lasts. A properly installed brick structure can go 100 years or more with basic upkeep. Compare that to wood siding that needs painting every few years, or vinyl that warps and fades.
                </p>
                <p>
                  No painting. No warping. No pest damage. Brick also provides natural insulation and fire resistance, which are practical benefits on top of the curb appeal. For homeowners in Bergen County and Passaic County looking at long-term value, brick is hard to beat.
                </p>
                <p>
                  In North Jersey's competitive real estate market, buyers notice brick. Neighborhoods like Glen Ridge and Rutherford have a strong association between brick exteriors and quality construction. That perception translates directly into resale value.
                </p>
                <p>
                  The key to keeping brick looking good long-term is timely tuckpointing. When mortar joints start to crack or recede, repointing them before water gets in will extend the life of the wall by decades.
                </p>
                <p>
                  The <CitationLink href="https://www.masoncontractors.org/" number={4}>Mason Contractors Association of America (MCAA)</CitationLink> certification program helps homeowners identify qualified masonry contractors committed to quality standards.
                </p>
              </div>
            </div>
          </div>
        </section>


        {/* YouTube Shorts Section */}
        <YouTubeShortsSection
          title="Brick Work in Action"
          videoIds={youtubeVideoIds}
        />

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground">
                Common questions about brick work in North Jersey
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-card rounded-xl border border-border p-6">
                  <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <RelatedServices currentCategory="masonry" />

        {/* CTA Section */}
        <CTASection
          serviceName="Brick Work"
          heading="Ready for Professional"
          highlightedHeading="Brick Work?"
        />

        {/* Citations Accordion */}
        <section className="py-8 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:text-foreground">
                    View Sources & Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      {citations.map((citation) => (
                        <div key={citation.id} className="text-sm">
                          <span className="text-muted-foreground">[{citation.id}]</span>{" "}
                          <a
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary hover:underline font-medium"
                          >
                            {citation.source}
                          </a>
                          <span className="text-muted-foreground"> — {citation.description}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default BrickWork;
